import { useMemo } from 'react';
import { Typography, Space, Tooltip, Button, Tag } from 'antd';
import {
  DollarOutlined,
  ShoppingCartOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { SmartDataTableProps } from '@src/components/SmartDataTable/types';
import { useInvoiceStatusRender } from './useInvoiceStatusRender';
import { useUserTimestampRender, useUserAvatarRender } from '@src/hooks';
import { MultilineTextField } from '@src/components/shared/fields';
import dayjs from 'dayjs';

const { Text } = Typography;

interface UseInvoiceColumnsProps {
  onCreateTransaction?: (transactionData: {
    transaction_type: 'invoice' | 'sell';
    invoice_id: number;
    amount: number;
    patient_id: string | number;
    customer_name?: string;
    type: string;
  }) => void;
}

export const useInvoiceColumns = (
  props?: UseInvoiceColumnsProps
): SmartDataTableProps['columns'] => {
  const { onCreateTransaction } = props || {};
  const renderInvoiceStatus = useInvoiceStatusRender();
  const userTimestampRender = useUserTimestampRender({
    size: 'small',
  });
  const userAvatarRender = useUserAvatarRender();

  const handleCreateTransaction = (record: any) => {
    if (record.type === 'invoice') {
      const transactionData = {
        transaction_type: 'invoice' as const,
        invoice_id: Number(record.id),
        amount: Number(record.due_amount || 0),
        patient_id: record.patient_id,
        type: 'PATIENT_INVOICE', // Set payment type to PATIENT_INVOICE
      };

      if (onCreateTransaction) {
        onCreateTransaction(transactionData);
      }
    } else if (record.type === 'sale') {
      const transactionData = {
        transaction_type: 'sell' as const,
        invoice_id: Number(record.id), // This will be used as s_id in the handler
        amount: Number(record.due_amount || 0),
        patient_id: record.patient_id,
        customer_name: record.customer_name,
        type: 'MEDICAL_SALES', // Set payment type to MEDICAL_SALES
      };

      if (onCreateTransaction) {
        onCreateTransaction(transactionData);
      }
    }
  };

  return useMemo(
    () => [
      {
        key: 'id',
        title: 'ID',
        dataIndex: 'id',
        minWidth: 80,
        ellipsis: true,
        render: (value: any, record: any) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Text
              style={{
                fontSize: '12px',
                color: '#8c8c8c',
                backgroundColor: '#f5f5f5',
                padding: '2px 6px',
                borderRadius: '4px',
              }}
            >
              #{value}
            </Text>
            <Tag
              color={record.type === 'sale' ? 'green' : 'blue'}
              style={{ fontSize: '10px', margin: 0 }}
            >
              {record.type === 'sale' ? 'MEDICAL' : 'CLINIC'}
            </Tag>
          </div>
        ),
      },
      {
        key: 'type_specific_info',
        title: 'Details',
        dataIndex: 'type_specific_info',
        minWidth: 300,
        render: (value: any, record: any) => {
          if (record.type === 'sale') {
            // Render sale items
            if (!record.sale_items || record.sale_items.length === 0)
              return '-';

            return (
              <div style={{ cursor: 'pointer' }}>
                {record.sale_items.map((item: any, index: number) => (
                  <div
                    key={index}
                    style={{
                      marginBottom:
                        index < record.sale_items.length - 1 ? '4px' : '0',
                    }}
                  >
                    <div
                      style={{
                        fontSize: '13px',
                        fontWeight: 500,
                        color: '#262626',
                      }}
                    >
                      {item.medicine?.name || item.item_name || '-'}
                    </div>
                    <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                      Qty: {item.quantity}
                    </div>
                  </div>
                ))}
              </div>
            );
          } else {
            // Render invoice items
            if (
              !record.clinic_invoice_items ||
              record.clinic_invoice_items.length === 0
            )
              return '-';

            return (
              <div style={{ cursor: 'pointer' }}>
                {record.clinic_invoice_items.map((item: any, index: number) => (
                  <div
                    key={index}
                    style={{
                      marginBottom:
                        index < record.clinic_invoice_items.length - 1
                          ? '4px'
                          : '0',
                    }}
                  >
                    <div
                      style={{
                        fontSize: '13px',
                        fontWeight: 500,
                        color: '#262626',
                      }}
                    >
                      {item.item_name}
                    </div>
                    <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                      {item.total_sessions} sessions • ₹
                      {Number(item.unit_price).toFixed(2)} • Qty:{' '}
                      {item.quantity}
                    </div>
                  </div>
                ))}
              </div>
            );
          }
        },
      },
      {
        key: 'customer_info',
        title: 'Treatment Details',
        dataIndex: 'customer_info',
        minWidth: 200,
        render: (value: any, record: any) => {
          // For invoices, show staff info
          return (
            <div style={{ display: 'flex', flexDirection: 'row', gap: '4px' }}>
              {record?.doctor && (
                <div style={{ marginBottom: '4px' }}>
                  <div style={{ fontSize: '11px', color: '#8c8c8c' }}>
                    Doctor
                  </div>
                  {userAvatarRender(record?.doctor)}
                </div>
              )}
              {record?.therapist && (
                <div style={{ marginBottom: '4px' }}>
                  <div style={{ fontSize: '11px', color: '#8c8c8c' }}>
                    Therapist
                  </div>
                  {userAvatarRender(record?.therapist)}
                </div>
              )}
              {record?.counsellor && (
                <div style={{ marginBottom: '4px' }}>
                  <div style={{ fontSize: '11px', color: '#8c8c8c' }}>
                    Counsellor
                  </div>
                  {userAvatarRender(record?.counsellor)}
                </div>
              )}
            </div>
          );
        },
      },
      {
        key: 'financial_summary',
        title: 'Financial Summary',
        dataIndex: 'financial_summary',
        minWidth: 190,
        render: (value: any, record: any) => {
          if (record._rowSpan === 0) return null;

          if (record.type === 'sale') {
            // For sales
            const subtotal = Number(record.subtotal) || 0;
            const discountAmount = Number(record.discount_amount) || 0;
            const taxAmount = Number(record.tax_amount) || 0;
            const billAmount = Number(record.bill_amount) || 0;
            const total = Number(record.total_amount) || 0;
            const dueAmount = Number(record.due_amount) || 0;
            const clinicDue = Number(record.clinic_due_amount) || 0;
            const paidAmount = billAmount - dueAmount;

            return (
              <div style={{ fontSize: '12px', lineHeight: '1.4' }}>
                {/* Main Total - Most Prominent */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '2px',
                  }}
                >
                  <Text
                    style={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#262626',
                    }}
                  >
                    Bill:
                  </Text>
                  <Text
                    style={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#52c41a',
                    }}
                  >
                    ₹{Number(billAmount).toFixed(2)}
                  </Text>
                </div>

                {/* Payment Status - Compact */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '2px',
                  }}
                >
                  <Text style={{ fontSize: '11px', color: '#8c8c8c' }}>
                    Paid/Due:
                  </Text>
                  <div
                    style={{
                      display: 'flex',
                      gap: '8px',
                      alignItems: 'center',
                    }}
                  >
                    <Text
                      style={{
                        fontSize: '11px',
                        fontWeight: 500,
                        color: '#52c41a',
                      }}
                    >
                      ₹{Number(paidAmount).toFixed(2)}
                    </Text>
                    <Text style={{ fontSize: '10px', color: '#d9d9d9' }}>
                      /
                    </Text>
                    <Text
                      style={{
                        fontSize: '11px',
                        fontWeight: 500,
                        color: dueAmount > 0 ? '#ff4d4f' : '#52c41a',
                      }}
                    >
                      ₹{Number(dueAmount).toFixed(2)}
                    </Text>
                  </div>
                </div>

                {/* Clinic Due Amount - Compact */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '2px',
                  }}
                >
                  <Text style={{ fontSize: '11px', color: '#8c8c8c' }}>
                    Clinic Due:
                  </Text>
                  <div
                    style={{
                      display: 'flex',
                      gap: '8px',
                      alignItems: 'center',
                    }}
                  >
                    <Text
                      style={{
                        fontSize: '11px',
                        fontWeight: 500,
                        color: clinicDue > 0 ? '#52c41a' : '#8c8c8c',
                      }}
                    >
                      ₹{Number(clinicDue).toFixed(2)}
                    </Text>
                  </div>
                </div>

                {/* Total Amount - Compact */}
                
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '2px',
                  }}
                >
                  <Text style={{ fontSize: '11px', color: '#8c8c8c' }}>
                    Total:
                  </Text>
                  <Text style={{ fontSize: '11px', color: '#52c41a' }}>
                    ₹{Number(total).toFixed(2)}
                  </Text>
                </div>

                {/* {taxAmount > 0 && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Text style={{ fontSize: '11px', color: '#8c8c8c' }}>
                      Tax:
                    </Text>
                    <Text style={{ fontSize: '11px', color: '#fa8c16' }}>
                      ₹{Number(taxAmount).toFixed(2)}
                    </Text>
                  </div>
                )} */}

                {/* Payment Status Badge - Only when due */}
                {/* {dueAmount > 0 && (
                <div
                  style={{
                    marginTop: '2px',
                    padding: '1px 4px',
                    backgroundColor: '#fff2f0',
                    border: '1px solid #ffccc7',
                    borderRadius: '3px',
                    textAlign: 'center',
                    width: 'fit-content',
                  }}
                >
                  <Text
                    style={{
                      fontSize: '9px',
                      color: '#ff4d4f',
                      fontWeight: 500,
                    }}
                  >
                    DUE
                  </Text>
                </div>
              )} */}
              </div>
            );
          } else {
            // For invoices (existing logic)
            const totalAmount = Number(record.total_amount) || 0;
            const billAmount = Number(record.bill_amount) || 0;
            const discountAmount = Number(record.overall_discount_amount) || 0;
            const gstAmount = Number(record.gst) || 0;
            const final = Number(record.final_amount) || 0;
            const balanceDue = Number(record.due_amount) || 0;
            const clinicDue = Number(record.clinic_due_amount) || 0;
            const dueAmount = Number(record.due_amount) || 0;
            const paidAmount = billAmount - dueAmount;

            return (
              <div style={{ fontSize: '12px', lineHeight: '1.4' }}>
                {/* Main Final Amount - Most Prominent */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '2px',
                  }}
                >
                  <Text
                    style={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#262626',
                    }}
                  >
                    Bill:
                  </Text>
                  <Text
                    style={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#52c41a',
                    }}
                  >
                    ₹{Number(billAmount).toFixed(2)}
                  </Text>
                </div>

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '2px',
                  }}
                >
                  <Text style={{ fontSize: '11px', color: '#8c8c8c' }}>
                    Paid/Due:
                  </Text>
                  <div
                    style={{
                      display: 'flex',
                      gap: '8px',
                      alignItems: 'center',
                    }}
                  >
                    <Text
                      style={{
                        fontSize: '11px',
                        fontWeight: 500,
                        color: '#52c41a',
                      }}
                    >
                      ₹{Number(paidAmount).toFixed(2)}
                    </Text>
                    <Text style={{ fontSize: '10px', color: '#d9d9d9' }}>
                      /
                    </Text>
                    <Text
                      style={{
                        fontSize: '11px',
                        fontWeight: 500,
                        color: dueAmount > 0 ? '#ff4d4f' : '#52c41a',
                      }}
                    >
                      ₹{Number(dueAmount).toFixed(2)}
                    </Text>
                  </div>
                </div>

                {/* Clinic Due Amount - Compact */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '2px',
                  }}
                >
                  <Text style={{ fontSize: '11px', color: '#8c8c8c' }}>
                    Clinic Due:
                  </Text>
                  <div
                    style={{
                      display: 'flex',
                      gap: '8px',
                      alignItems: 'center',
                    }}
                  >
                    <Text
                      style={{
                        fontSize: '11px',
                        fontWeight: 500,
                        color: clinicDue > 0 ? '#52c41a' : '#8c8c8c',
                      }}
                    >
                      ₹{Number(clinicDue).toFixed(2)}
                    </Text>
                  </div>
                </div>

                {/* Total, Discount and GST - Compact */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Text style={{ fontSize: '11px', color: '#8c8c8c' }}>
                    Total:
                  </Text>
                  <div
                    style={{
                      display: 'flex',
                      gap: '4px',
                      alignItems: 'center',
                    }}
                  >
                    <Text style={{ fontSize: '11px', color: '#262626' }}>
                      ₹{Number(totalAmount).toFixed(2)}
                    </Text>
                  </div>
                </div>

                {/* {gstAmount > 0 && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Text style={{ fontSize: '11px', color: '#8c8c8c' }}>
                      GST:
                    </Text>
                    <Text style={{ fontSize: '11px', color: '#fa8c16' }}>
                      ₹{Number(gstAmount).toFixed(2)}
                    </Text>
                  </div>
                )} */}
              </div>
            );
          }
        },
        onCell: (record: any) => ({
          rowSpan: record._rowSpan || 1,
        }),
      },
      {
        key: 'status',
        title: 'Status',
        dataIndex: 'status',
        minWidth: 120,
        render: (value: any, record: any) => {
          if (record.type === 'sale') {
            return renderInvoiceStatus(record.payment_status);
          } else {
            // For invoices, render invoice status
            return renderInvoiceStatus(record.invoice_status);
          }
        },
      },
      {
        key: 'notes_remark',
        title: 'Notes/Remark',
        dataIndex: 'notes_remark',
        minWidth: 200,
        render: (value: any, record: any) => {
          const text = record.type === 'sale' ? record.notes : record.remark;
          return (
            <MultilineTextField
              text={text}
              fontSize="13px"
              lineHeight="1.4"
              marginTop="0px"
              marginBottom="0px"
              showLabel={false}
            />
          );
        },
      },
      {
        key: 'actions',
        title: 'Payment',
        dataIndex: 'actions',
        minWidth: 120,
        render: (value: any, record: any) => {
          // In Ant Design table render function, the second parameter is the record
          if (!record) return null;

          const dueAmount = Number(record.due_amount || 0);
          const canCreateTransaction = dueAmount > 0;

          if (!canCreateTransaction) return null;

          return (
            <Tooltip
              title={`Create Invoice Transaction - ₹${dueAmount.toFixed(2)} due`}
            >
              <Button
                type="primary"
                size="small"
                icon={<DollarOutlined />}
                onClick={() => handleCreateTransaction(record)}
                style={{
                  fontSize: '11px',
                  height: '28px',
                  padding: '4px 8px',
                }}
              >
                Pay ₹{dueAmount.toFixed(2)}
              </Button>
            </Tooltip>
          );
        },
      },
      {
        key: 'user_timestamp',
        title: 'User & Timestamp',
        dataIndex: 'user_info',
        sorter: false,
        minWidth: 200,
        render: (value: any, record: any) => userTimestampRender(record),
      },
    ],
    [
      renderInvoiceStatus,
      userTimestampRender,
      userAvatarRender,
      handleCreateTransaction,
      onCreateTransaction,
    ]
  );
};
